import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { initialCategoriaAssistenciaListaFiltersState, useCategoriaAssistenciaListaFilters } from "../hooks/CategoriaAssistencia_lista/filters";
import { useCategoriaAssistenciaListaPagination } from "../hooks/CategoriaAssistencia_lista/pagination";
import { CategoriaAssistenciaLista } from "../../domain/models/categoria_assistencia_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { CategoriaAssistenciaForm } from "../schemas/CategoriaAssistencia_lista/categoria_assistencia";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

/**
 * LoadCategoriasAssistenciaFuncState
 */

type LoadCategoriasAssistenciaFuncState = {
  field?: number | null;
  value?: string | null;
  status?: number | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useCategoriaAssistenciaListaViewModel
 */

export const useCategoriaAssistenciaListaViewModel = () => {

  const {
    entregaObras: {
      getAllCategoriaAssistenciaUseCase,
      createCategoriaAssistenciaUseCase,
      updateCategoriaAssistenciaUseCase,
      deleteCategoriaAssistenciaUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    }
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const debouncedHandlerRef = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [categoriasAssistencia, setCategoriasAssistencia] = useState<CategoriaAssistenciaLista[]>();

  const [selectedCategoriaAssistencia, setSelectedCategoriaAssistencia] = useState<CategoriaAssistenciaLista>();

  const [categoriasAssistenciaModal, setCategoriasAssistenciaModal] = useState(false);
  const [categoriasAssistenciaDeleteModal, setCategoriasAssistenciaDeleteModal] = useState(false);

  const filters = useCategoriaAssistenciaListaFilters();
  const pagination = useCategoriaAssistenciaListaPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  /**
   * Funcoes de carregamento
   */

  const loadCategoriasAssistencia = useCallback(async (fs?: LoadCategoriasAssistenciaFuncState) => {
    try {
      const filteredField = evaluateParamValue(fs?.field, filters.field);
      const filteredValue = evaluateParamValue(fs?.value, filters.value);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllCategoriaAssistenciaUseCase.getAllCategoriaAssistencia(
        filteredField,
        filteredValue,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setCategoriasAssistencia(getAllCategoriaAssistenciaUseCase.items);
      pagination.setTotalItems(getAllCategoriaAssistenciaUseCase.totalItems);
    } catch (e: unknown) {
      setCategoriasAssistencia(undefined);
      setErrorMessage(processError(e, "Falha ao buscar categorias de assistência!")[0]);
    }
  }, [
    filters,
    pagination,
    getAllCategoriaAssistenciaUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {

      await loadCategoriasAssistencia();

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadCategoriasAssistencia();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCategoriasAssistencia,
    pushErrorNotification,
  ]);

  const handleCategoriaAssistenciaOpenState = useCallback(async (idCategoriaAssistencia: number | null, openState: boolean) => {
    if (openState) {
      if (idCategoriaAssistencia && categoriasAssistencia) {
        setSelectedCategoriaAssistencia(categoriasAssistencia.find(c => c.idCategoriaAssistencia === idCategoriaAssistencia));
      } else {
        setSelectedCategoriaAssistencia(undefined);
      }
    }
    setCategoriasAssistenciaModal(openState);
  }, [
    categoriasAssistencia,
  ]);

  const handleCategoriaAssistenciaSubmit = useCallback(async (form: CategoriaAssistenciaForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedCategoriaAssistencia) {
        await updateCategoriaAssistenciaUseCase.updateCategoriaAssistencia(
          selectedCategoriaAssistencia.idCategoriaAssistencia,
          form.status,
          form.categoria,
        );
      } else {
        await createCategoriaAssistenciaUseCase.createCategoriaAssistencia(
          form.status,
          form.categoria,
        );
      }

      await loadCategoriasAssistencia();

      setSelectedCategoriaAssistencia(undefined);
      setCategoriasAssistenciaModal(false);

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedCategoriaAssistencia,
    createCategoriaAssistenciaUseCase,
    updateCategoriaAssistenciaUseCase,
    loadCategoriasAssistencia,
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleCategoriaAssistenciaDeleteOpenState = useCallback(async (idCategoriaAssistencia: number | null, openState: boolean) => {
    if (openState) {
      if (idCategoriaAssistencia && categoriasAssistencia) {
        setSelectedCategoriaAssistencia(categoriasAssistencia.find(c => c.idCategoriaAssistencia === idCategoriaAssistencia));
      } else {
        setSelectedCategoriaAssistencia(undefined);
      }
    }
    setCategoriasAssistenciaDeleteModal(openState);
  }, [
    categoriasAssistencia,

  ]);

  const handleCategoriaAssistenciaDeleteSubmit = useCallback(async () => {
    if (selectedCategoriaAssistencia) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteCategoriaAssistenciaUseCase.deleteCategoriaAssistencia(
          selectedCategoriaAssistencia.idCategoriaAssistencia,
        );

        await loadCategoriasAssistencia();

        setSelectedCategoriaAssistencia(undefined);
        setCategoriasAssistenciaDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedCategoriaAssistencia,
    deleteCategoriaAssistenciaUseCase,
    loadCategoriasAssistencia,
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useCategoriaAssistenciaListaPagination.setState({ page: page + 1 });
      await loadCategoriasAssistencia({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCategoriasAssistencia,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useCategoriaAssistenciaListaPagination.setState({ page: 1, rowsPerPage });
      await loadCategoriasAssistencia({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCategoriasAssistencia,
    pushErrorNotification,
  ]);

  const handleFieldChange = useCallback(async (field: number | null) => {
    try {
      setLoading(true);
      useCategoriaAssistenciaListaFilters.setState({ field });
      useCategoriaAssistenciaListaPagination.setState({ page: 1 });
      await loadCategoriasAssistencia({ field, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCategoriasAssistencia,
    pushErrorNotification,
  ]);

  const handleValueChange = useDebouncedCallback(debouncedHandlerRef, async (value: string | null) => {
    useCategoriaAssistenciaListaFilters.setState({ value });
    return async () => {
      try {
        setLoading(true);
        useCategoriaAssistenciaListaPagination.setState({ page: 1 });
        await loadCategoriasAssistencia({ value, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadCategoriasAssistencia,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number | null) => {
    try {
      setLoading(true);
      useCategoriaAssistenciaListaFilters.setState({ status });
      useCategoriaAssistenciaListaPagination.setState({ page: 1 });
      await loadCategoriasAssistencia({ status, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCategoriasAssistencia,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useCategoriaAssistenciaListaFilters.setState({ ...initialCategoriaAssistenciaListaFiltersState });
      useCategoriaAssistenciaListaPagination.setState({ page: 1 });
      await loadCategoriasAssistencia({ ...initialCategoriaAssistenciaListaFiltersState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao atualizar filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCategoriasAssistencia,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedCategoriaAssistencia) {
      changeHistory.handleOpenChangeHistory(selectedCategoriaAssistencia.idCategoriaAssistencia, LogTipoTabela.CATEGORIA_ASSISTENCIA, true);
    }
  }, [
    selectedCategoriaAssistencia,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    categoriasAssistencia: categoriasAssistencia ?? [],
    selectedCategoriaAssistencia: selectedCategoriaAssistencia ?? null,
    categoriasAssistenciaModal,
    categoriasAssistenciaDeleteModal,
    filters,
    pagination,
    changeHistory,
    handleReload,
    handleCategoriaAssistenciaOpenState,
    handleCategoriaAssistenciaSubmit,
    handleCategoriaAssistenciaDeleteOpenState,
    handleCategoriaAssistenciaDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleFieldChange,
    handleValueChange,
    handleStatusChange,
    handleClearFilters,
    handleOpenChangeHistory,
  };
}